import { useMemo } from 'react';
import {
  SKILL_DIVE_ASSIGN_TOOLTIP,
  SKILL_DIVE_REMOVE_TOOLTIP,
  SKILL_DIVE_TOGGLE_TOOLTIPS,
  SkillDiveToggleReason,
} from 'constants/skill_dive';
import { CONTENT_TYPES } from 'constants/playlists';
import useLicenseAvailability, { LicensePool } from './useLicenseAvailability';

export interface SkillDiveToggleAccount {
  account_available_addons?: ({ id: string; name: string } & LicensePool)[] | null;
}

// A table/profile user (`license`, `license_addons`) or an invitee being composed
// (`has_license`, `has_skill_dive`); pass whichever shape the caller has.
export interface SkillDiveTogglePerson {
  license?: unknown;
  license_addons?: { id: string; addon_name?: string }[];
  has_license?: boolean;
  has_skill_dive?: boolean;
}

export interface SkillDiveToggleStateInput {
  account?: SkillDiveToggleAccount | null;
  user?: SkillDiveTogglePerson | null;
  invitee?: SkillDiveTogglePerson | null;
  /** From `SessionContext`'s `allows.can_unnasign_license`. */
  can_unnasign_license: boolean;
  /** Per-reason copy overrides for another audience; omitted reasons keep the shared copy. */
  tooltips?: Partial<Record<SkillDiveToggleReason, string>>;
}

export interface SkillDiveToggleState {
  disabled: boolean;
  reason: SkillDiveToggleReason | null;
  tooltip: string;
  /** The assigned Skill Dive license_addon id, or null if unassigned. */
  assignedId: string | null;
}

const hasTrainingLicense = (person: SkillDiveTogglePerson): boolean =>
  person.license !== undefined ? Boolean(person.license) : Boolean(person.has_license);

// `license_addons` holds every addon type the person has, so match on `addon_name`.
export const findAssignedSkillDive = <T extends { addon_name?: string }>(person: {
  license_addons?: T[];
}) => person.license_addons?.find((addon) => addon.addon_name === CONTENT_TYPES.SKILL_DIVE);

const hasSkillDiveAssigned = (person: SkillDiveTogglePerson): boolean =>
  person.license_addons !== undefined
    ? Boolean(findAssignedSkillDive(person))
    : Boolean(person.has_skill_dive);

// Single source of the Skill Dive toggle's disabled state, reason and tooltip.
const useSkillDiveToggleState = ({
  account,
  user,
  invitee,
  can_unnasign_license,
  tooltips,
}: SkillDiveToggleStateInput): SkillDiveToggleState => {
  // Catalog ids differ per environment, so find the Skill Dive pool by `name`.
  const pool = account?.account_available_addons?.find(
    (addon) => addon.name === CONTENT_TYPES.SKILL_DIVE
  );
  const availability = useLicenseAvailability(pool);

  return useMemo(() => {
    const person = user ?? invitee ?? {};
    const assignedId = findAssignedSkillDive(person)?.id ?? null;
    const tooltipFor = (reason: SkillDiveToggleReason) =>
      tooltips?.[reason] ?? SKILL_DIVE_TOGGLE_TOOLTIPS[reason];

    if (availability.mode === 'not_on_plan') {
      // After a plan change, still let the caller remove a seat that was already granted.
      if (hasSkillDiveAssigned(person)) {
        return can_unnasign_license
          ? { disabled: false, reason: null, tooltip: SKILL_DIVE_REMOVE_TOOLTIP, assignedId }
          : {
              disabled: true,
              reason: SkillDiveToggleReason.NON_TRANSFERABLE,
              tooltip: tooltipFor(SkillDiveToggleReason.NON_TRANSFERABLE),
              assignedId,
            };
      }
      return {
        disabled: true,
        reason: SkillDiveToggleReason.NOT_ON_PLAN,
        tooltip: tooltipFor(SkillDiveToggleReason.NOT_ON_PLAN),
        assignedId,
      };
    }

    if (!hasTrainingLicense(person)) {
      return {
        disabled: true,
        reason: SkillDiveToggleReason.NO_TRAINING,
        tooltip: tooltipFor(SkillDiveToggleReason.NO_TRAINING),
        assignedId,
      };
    }

    if (hasSkillDiveAssigned(person)) {
      return can_unnasign_license
        ? { disabled: false, reason: null, tooltip: SKILL_DIVE_REMOVE_TOOLTIP, assignedId }
        : {
            disabled: true,
            reason: SkillDiveToggleReason.NON_TRANSFERABLE,
            tooltip: tooltipFor(SkillDiveToggleReason.NON_TRANSFERABLE),
            assignedId,
          };
    }

    // Only a real count can run out; an unlimited pool is never exhausted.
    if (availability.mode === 'count' && (availability.value ?? 0) <= 0) {
      return {
        disabled: true,
        reason: SkillDiveToggleReason.EXHAUSTED,
        tooltip: tooltipFor(SkillDiveToggleReason.EXHAUSTED),
        assignedId,
      };
    }

    // Assignable, but warn up front when the caller couldn't unassign it later.
    return can_unnasign_license
      ? { disabled: false, reason: null, tooltip: SKILL_DIVE_ASSIGN_TOOLTIP, assignedId }
      : {
          disabled: false,
          reason: null,
          tooltip: tooltipFor(SkillDiveToggleReason.NON_TRANSFERABLE),
          assignedId,
        };
  }, [availability.mode, availability.value, can_unnasign_license, user, invitee, tooltips]);
};

// An unsent invitation or a link setting doesn't take a seat yet, so its state ignores the plan's
// removal rule. Warn up front instead, as the users table does, when the plan can't take it back.
export const skillDiveTooltip = (
  state: Pick<SkillDiveToggleState, 'disabled' | 'tooltip'>,
  canUnassignLicense: boolean
): string | null => {
  if (state.disabled) return state.tooltip;
  if (canUnassignLicense) return null;
  return SKILL_DIVE_TOGGLE_TOOLTIPS[SkillDiveToggleReason.NON_TRANSFERABLE];
};

export default useSkillDiveToggleState;
